/**
 * `<PrepProvenanceLine>` — attribution sub-line beneath the active-stage
 * header. Tells the recruiter which prior analyses informed this prep.
 *
 * Phase 2.6 — interview-prep-spec.md §2.2 / §2.3 / §2.4.
 *
 * Three cases:
 *   1. **Enriched** — one-or-more prior analyses contributed; renders
 *      "Generated {date} · Enriched by: {sources}".
 *   2. **Empty chain** — first interview stage; renders
 *      "Generated from job + resume only — no prior analyses to enrich."
 *   3. **Skipped predecessor** — note rendered after the sources list:
 *      "{X} stage was skipped." (silent vs. failed-chain is a separate
 *      cofounder canon — see §4 invariants).
 *
 * Cofounder ruling 3 (Phase 2.6): chain failure (predecessor errored)
 * is **silent** — NO banner, NO warn-toast. This component never
 * surfaces failure copy; it only attributes what successfully fed in.
 *
 * Props:
 *   `generatedAt` — ISO timestamp of the prep row.
 *   `sources` — list of source descriptions (e.g.
 *               "Phone Screen analysis (5/3)"). Empty array → empty-chain
 *               copy.
 *   `skippedStageName` — optional; if set, appended as a note about
 *                        an explicitly-skipped predecessor (not failure).
 */
import type { JSX } from "react";

interface PrepProvenanceLineProps {
  generatedAt: string;
  sources: string[];
  skippedStageName?: string;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function PrepProvenanceLine({
  generatedAt,
  sources,
  skippedStageName,
}: PrepProvenanceLineProps): JSX.Element {
  const dateStr = formatDate(generatedAt);

  if (sources.length === 0) {
    return (
      <p
        className="text-xs text-text-secondary"
        data-testid="prep-provenance-empty"
      >
        Generated {dateStr} from job + resume only — no prior analyses to
        enrich.
      </p>
    );
  }

  return (
    <p
      className="text-xs text-text-secondary"
      data-testid="prep-provenance"
    >
      Generated {dateStr} · Enriched by: {sources.join(", ")}
      {skippedStageName ? (
        <span className="ml-1 text-text-tertiary">
          ({skippedStageName} stage was skipped.)
        </span>
      ) : null}
    </p>
  );
}
